import React from "react";
import PermitSitePhotosTab from "../../components/PermitSitePhotosTab";
import { Permit } from "../../models/Permit";
import { AppState } from "../../store";
import { connect } from "react-redux";
import { addSitePhoto, deleteSitePhoto } from "./actions";
import { User } from "../../models/User";
import { Guid } from "guid-typescript";
import { RawFile } from "../../models/RawFile";
import PermissionService from "../../PermissionService";
import { ISSUE_PERMIT, ISSUED, READY_TO_ISSUE, RUN_EIGHT_POINT_CHECKLIST } from "../../constants";
import { Role } from "../../models/Role";
import EditSitePhotoModal from "../../components/EditSitePhotoModal";

interface PermitSitePhotosTabContainerProps {
    permit: Permit | null;
    currentUser: User | null;
    addSitePhoto: typeof addSitePhoto;
    deleteSitePhoto: typeof deleteSitePhoto;
    isReadOnly: boolean;
    clientRoles: Role[];
    projectUsers: User[];
}

const PermitSitePhotosTabContainer: React.FC<PermitSitePhotosTabContainerProps> = ({
    currentUser,
    permit,
    addSitePhoto,
    deleteSitePhoto,
    isReadOnly,
    clientRoles,
    projectUsers,
}) => {
    const [showModal, setShowModal] = React.useState(false);
    const [selectedPhotoPath, setSelectedPhotoPath] = React.useState("");
    const generateFileKey = (): string => {
        return "file-" + Guid.create().toString();
    };

    const handleSitePhotoCaptured = (dataUrl: string) => {
        setShowModal(true);
        setSelectedPhotoPath(dataUrl);
    };

    const handleSitePhotoAdded = async (comments: string) => {
        const fileKey = generateFileKey();

        addSitePhoto(
            fileKey,
            { fileName: fileKey, contentType: "image/png", dataUrl: selectedPhotoPath } as RawFile,
            currentUser!.id,
            comments
        );

        setShowModal(false);
        setSelectedPhotoPath("");
    };

    const handeleSitePhotoDeleted = async (sitePhotoId: string) => {
        deleteSitePhoto(sitePhotoId);
    };

    const handleEditSitePhotoModalClose = () => {
        setShowModal(false);
        setSelectedPhotoPath("");
    };

    if (!permit || !currentUser || !clientRoles) return null;
    const ableToIssuePermit = PermissionService.userHasPermission(
        currentUser,
        ISSUE_PERMIT,
        clientRoles,
        permit.project.id
    );
    const ableToRunDailyCheckList = PermissionService.userHasPermission(
        currentUser,
        RUN_EIGHT_POINT_CHECKLIST,
        clientRoles,
        permit.project.id
    );
    const ableToAddSitePhotos =
        (permit.status === READY_TO_ISSUE && ableToIssuePermit) ||
        (permit.status === ISSUED && (ableToIssuePermit || ableToRunDailyCheckList));

    return (
        <>
            <EditSitePhotoModal
                sitePhotoPath={selectedPhotoPath}
                open={showModal}
                onSubmit={handleSitePhotoAdded}
                onClose={handleEditSitePhotoModalClose}
            />

            <PermitSitePhotosTab
                permitId={permit.id}
                sitePhotos={permit.sitePhotos?.filter((sp) => !sp.pendingDelete)}
                onSitePhotoCaptured={handleSitePhotoCaptured}
                onSitePhotoDeleted={handeleSitePhotoDeleted}
                isReadOnly={isReadOnly || !ableToIssuePermit}
                ableToAddPhotos={ableToAddSitePhotos}
                projectUsers={projectUsers}
            />
        </>
    );
};

function mapStateToProps(state: AppState) {
    return {
        permit: state.permitPage.permit,
        currentUser: state.user.currentUser,
        clientRoles: state.myPermits.clientRoles,
        projectUsers: state.permitAssignments.projectUsers,
    };
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        addSitePhoto: (sitePhotoFileKey: string, photoFile: RawFile, createdById: string, comments: string) =>
            dispatch(addSitePhoto(sitePhotoFileKey, photoFile, createdById, comments)),
        deleteSitePhoto: (sitePhotoId: string) => dispatch(deleteSitePhoto(sitePhotoId)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PermitSitePhotosTabContainer);
